import React, { useState, useContext } from 'react';
import { ThemeContext } from '../contexts/ThemeContext';
import { FriendsContext } from '../contexts/FriendsContext';
import { ResultsContext } from '../contexts/ResultsContext';
import MatchingTournamentButton from './MatchingTournamentButton';
import ConsolidatedPlayersList from './ConsolidatedPlayersList';
import Modal from './Modal';
import ScoreGraph from './ScoreGraph';
import './PlayersList.css';

/**
 * PlayersList component displays a list of players for a game
 * 
 * @param {Object} props - Component props
 * @param {Array} props.players - Array of player objects
 * @param {string} props.currentUsername - Username of the current user
 * @param {string} props.gameTitle - Title of the game
 * @param {string} props.image - URL of the game image
 */
function PlayersList({ players, currentUsername, gameTitle, image }) {
  const { theme } = useContext(ThemeContext);
  const { isFriend, addFriend, removeFriend } = useContext(FriendsContext);
  const { results } = useContext(ResultsContext);
  const [sortBy, setSortBy] = useState('rank');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [consolidatedData, setConsolidatedData] = useState(null);
  const [isGraphModalOpen, setIsGraphModalOpen] = useState(false);
  const [showFriendsOnly, setShowFriendsOnly] = useState(false);

  // Handle sort change
  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle sort direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field and default to ascending
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  // Handle friend button click
  const handleFriendButtonClick = (username) => {
    if (isFriend(username)) {
      removeFriend(username);
    } else {
      addFriend(username);
    }
  };

  // Handle when matching tournament data is found
  const handleMatchingDataFound = (data) => {
    setConsolidatedData(data);
  };

  // Handle going back to original view
  const handleBackToOriginal = () => {
    setConsolidatedData(null);
  };

  // If we have consolidated data, show the consolidated view
  if (consolidatedData) {
    return (
      <ConsolidatedPlayersList
        currentGame={consolidatedData.currentGame}
        matchingGame={consolidatedData.matchingGame}
        currentUsername={currentUsername}
        onBackToOriginal={handleBackToOriginal}
        users={results?.users}
      />
    );
  }

  // Sort and filter players
  const sortedPlayers = [...players]
    .filter(player => {
      const matchesSearch = player.userName.toLowerCase().includes(searchTerm.toLowerCase());
      const isSelf = player.userName.toLowerCase() === currentUsername?.toLowerCase();
      const matchesFriendFilter = !showFriendsOnly || isFriend(player.userName) || isSelf;
      return matchesSearch && matchesFriendFilter;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'rank':
          comparison = a.rank - b.rank;
          break;
        case 'userName':
          comparison = a.userName.localeCompare(b.userName);
          break;
        case 'score':
          comparison = a.score - b.score;
          break;
        case 'points':
          comparison = a.points - b.points;
          break;
        default:
          comparison = a.rank - b.rank;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  return (
    <div className={`players-list-container ${theme}`}>
      {/* Game Header with Backglass Image */}
      <div className="players-list-image-header">
        {image && (
          <img 
            src={image} 
            alt={gameTitle} 
            className="backglass-image"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <div className="image-overlay">
          <div className="game-title-large">{gameTitle}</div>
        </div>
      </div>
      {/* Add the matching tournament button if we have a tournament type */}
      {results?.type && (results.type === 'atgames-4k' || results.type === 'atgames-hd') && (
        <MatchingTournamentButton
          gameTitle={gameTitle}
          currentLeaderboard={players}
          onMatchingDataFound={handleMatchingDataFound}
        />
      )}
      
      <div className="players-list-actions">
        <div className="players-list-search">
          <input
            type="text"
            placeholder="Search players..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={theme}
            autoComplete="off"
            data-lpignore="true"
          />
        </div>

        <div className="players-list-filter">
          <div className={`filter-button-group ${theme}`}>
            <button 
              className={`filter-button ${!showFriendsOnly ? 'active' : ''} ${theme}`}
              onClick={() => setShowFriendsOnly(false)}
            >
              All
            </button>
            <button 
              className={`filter-button ${showFriendsOnly ? 'active' : ''} ${theme}`}
              onClick={() => setShowFriendsOnly(true)}
            >
              Friends
            </button>
          </div>
        </div>
        
        <button 
          className={`view-graph-button ${theme}`}
          onClick={() => setIsGraphModalOpen(true)}
        >
          View Score Graph
        </button>
      </div>
      
      <div className="players-list-content">
        <div className={`players-list-header ${theme}`}>
          <div className="header-rank" onClick={() => handleSort('rank')}>
            RANK {sortBy === 'rank' && (sortDirection === 'asc' ? '↑' : '↓')}
          </div>
          <div className="header-player" onClick={() => handleSort('userName')}>
            PLAYER {sortBy === 'userName' && (sortDirection === 'asc' ? '↑' : '↓')}
          </div>
          <div className="header-score" onClick={() => handleSort('score')}>
            SCORE {sortBy === 'score' && (sortDirection === 'asc' ? '↑' : '↓')}
          </div>
          <div className="header-friend"></div>
        </div>

        <div className="players-list-scroll">
          {sortedPlayers.map((player) => (
            <div 
              key={player.userName} 
              className={`score-item ${theme} ${player.userName.toLowerCase() === currentUsername?.toLowerCase() ? 'current-user' : ''}`}
            >
              <div className="item-rank">#{player.rank}</div>
              <div className="item-player">
                {results?.users?.[player.userName]?.avatar_path && (
                  <div 
                    className="player-avatar-circle" 
                    style={{ backgroundColor: results.users[player.userName].background_color_hex }}
                  >
                    <img 
                      src={results.users[player.userName].avatar_path} 
                      alt={player.userName} 
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
                <div className="player-details">
                  <span 
                    className="player-name"
                    style={{ color: results?.users?.[player.userName]?.background_color_hex }}
                  >
                    {player.userName}
                  </span>
                  {player.initials && <span className="player-initials">({player.initials})</span>}
                </div>
              </div>
              <div className="item-score">{player.score?.toLocaleString()}</div>
              <div className="item-friend">
                {player.userName.toLowerCase() !== currentUsername?.toLowerCase() && (
                  <button 
                    className={`friend-button ${isFriend(player.userName) ? 'remove-friend' : 'add-friend'}`}
                    onClick={() => handleFriendButtonClick(player.userName)}
                    title={isFriend(player.userName) ? 'Remove friend' : 'Add friend'}
                  >
                    {isFriend(player.userName) ? <i className="fa-solid fa-check"></i> : <i className="fa-solid fa-user-plus"></i>}
                  </button>
                )}
              </div>
            </div>
          ))}
          {sortedPlayers.length === 0 && (
            <div className="no-results">No players found</div>
          )}
        </div>
      </div>
      
      {/* Score Graph Modal */}
      <Modal 
        isOpen={isGraphModalOpen} 
        onClose={() => setIsGraphModalOpen(false)} 
        title={`Score Distribution - ${gameTitle}`}
        size="large"
      >
        <ScoreGraph 
          players={players} 
          currentUsername={currentUsername}
          matchingPlayers={consolidatedData?.matchingGame?.leaderboard}
          gameTitle={gameTitle}
        />
      </Modal>
    </div>
  );
}

export default PlayersList;